/**
 * Mock API Service for Wireframe Prototypes
 * Simulates real API calls with realistic delays and data
 */

// Types
export interface Crisis {
  id: string;
  type: 'cholera' | 'flooding' | 'drought' | 'conflict' | 'displacement';
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  region: string;
  locations: string[];
  coordinates: [number, number];
  cases?: number;
  affectedPopulation: number;
  interventionWindow: string;
  confidence: number;
  timestamp: string;
  status: 'active' | 'monitoring' | 'resolved';
  trend?: string;
}

export interface Alert {
  id: string;
  crisisId?: string;
  type: 'cholera' | 'flooding' | 'drought' | 'conflict' | 'displacement';
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  region: string;
  locations: string;
  window: string;
  confidence: string;
  timestamp: string;
  description?: string;
  source?: string;
  acknowledged?: boolean;
}

export interface Team {
  id: string;
  name: string;
  type: 'WASH' | 'Health' | 'Protection' | 'Shelter' | 'Food Security' | 'Assessment';
  location: string;
  coordinates: [number, number];
  members: number;
  status: 'Active' | 'En Route' | 'Standby' | 'Off Duty';
  lastCheckIn: string;
  operation?: string;
}

export interface Resource {
  id: string;
  name: string;
  category: 'Medical' | 'WASH' | 'Shelter' | 'Food' | 'Transport';
  current: number;
  capacity: number;
  unit: string;
  status: 'adequate' | 'low' | 'critical';
  location: string;
}

export interface DataSource {
  id: string;
  name: string;
  type: string;
  status: 'online' | 'delayed' | 'offline';
  lastUpdate: string;
  events: number;
}

export interface CashLocation {
  id: string;
  name: string;
  region: string;
  coordinates: [number, number];
  affectedPopulation: number;
  eligibleHouseholds: number;
  transferAmount: number;
  demographics: {
    gender: { male: number; female: number };
    ageGroups: { children: number; adults: number; elderly: number };
    vulnerabilityScore: number;
  };
  marketStatus: 'functional' | 'partially_functional' | 'non_functional';
  securityLevel: 'stable' | 'moderate' | 'volatile';
}

// Simulated delay for realistic API feel
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock Data Store
let mockCrises: Crisis[] = [
  {
    id: 'cholera-somali-2025',
    type: 'cholera',
    title: 'Cholera Outbreak',
    severity: 'critical',
    region: 'Somali',
    locations: ['Jijiga', 'Kebridehar'],
    coordinates: [42.79, 9.35],
    cases: 247,
    affectedPopulation: 180000,
    interventionWindow: '46h',
    confidence: 94,
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    status: 'active',
    trend: '+34 in 24h',
  },
  {
    id: 'flooding-oromia-2025',
    type: 'flooding',
    title: 'Flooding Risk',
    severity: 'high',
    region: 'Oromia',
    locations: ['Bale', 'West Arsi'],
    coordinates: [39.76, 7.0],
    affectedPopulation: 45000,
    interventionWindow: '36h',
    confidence: 87,
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    status: 'active',
    trend: '36h warning',
  },
  {
    id: 'drought-afar-2025',
    type: 'drought',
    title: 'Drought Monitoring',
    severity: 'medium',
    region: 'Afar',
    locations: ['Zone 1', 'Zone 2'],
    coordinates: [40.0, 11.5],
    affectedPopulation: 95000,
    interventionWindow: '10d',
    confidence: 72,
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    status: 'monitoring',
    trend: 'Stable',
  },
];

let mockAlerts: Alert[] = [
  {
    id: 'alert-001',
    crisisId: 'cholera-somali-2025',
    type: 'cholera',
    title: 'Cholera Outbreak',
    severity: 'critical',
    region: 'Somali Region',
    locations: 'Jijiga, Kebridehar',
    window: '46h',
    confidence: '94%',
    timestamp: '2h ago',
    description: 'Confirmed cholera cases rising rapidly. Water contamination suspected.',
    source: 'MOH PHEM',
    acknowledged: false,
  },
  {
    id: 'alert-002',
    crisisId: 'flooding-oromia-2025',
    type: 'flooding',
    title: 'Flooding Risk',
    severity: 'high',
    region: 'Oromia Region',
    locations: 'Bale, West Arsi',
    window: '36h',
    confidence: '87%',
    timestamp: '4h ago',
    description: 'Heavy rainfall forecast. River levels rising.',
    source: 'Ethiopian Met Institute',
    acknowledged: true,
  },
  {
    id: 'alert-003',
    crisisId: 'drought-afar-2025',
    type: 'drought',
    title: 'Drought Monitoring',
    severity: 'medium',
    region: 'Afar Region',
    locations: 'Zone 1, Zone 2',
    window: '240h',
    confidence: '72%',
    timestamp: '1d ago',
    description: 'Vegetation stress indicators elevated. Pasture conditions deteriorating.',
    source: 'FEWS NET',
    acknowledged: true,
  },
];

let mockTeams: Team[] = [
  { id: 'team-001', name: 'WASH Team Alpha', type: 'WASH', location: 'Jijiga', coordinates: [42.5, 9.2], members: 6, status: 'Active', lastCheckIn: '15 min ago', operation: 'OP-001' },
  { id: 'team-002', name: 'Health Team Bravo', type: 'Health', location: 'Kebridehar', coordinates: [44.0, 6.5], members: 5, status: 'Active', lastCheckIn: '30 min ago', operation: 'OP-001' },
  { id: 'team-003', name: 'Assessment Team', type: 'Assessment', location: 'Gode', coordinates: [43.45, 5.95], members: 4, status: 'En Route', lastCheckIn: '1h ago', operation: 'OP-001' },
  { id: 'team-004', name: 'Shelter Team Delta', type: 'Shelter', location: 'Dire Dawa', coordinates: [42.0, 9.6], members: 8, status: 'Standby', lastCheckIn: '2h ago' },
  { id: 'team-005', name: 'Protection Team Echo', type: 'Protection', location: 'Harar', coordinates: [42.12, 9.31], members: 4, status: 'Active', lastCheckIn: '45 min ago', operation: 'OP-002' },
];

let mockResources: Resource[] = [
  { id: 'res-001', name: 'ORS Sachets', category: 'Medical', current: 12500, capacity: 50000, unit: 'units', status: 'low', location: 'Jijiga Warehouse' },
  { id: 'res-002', name: 'IV Fluids', category: 'Medical', current: 3200, capacity: 10000, unit: 'liters', status: 'critical', location: 'Jijiga Warehouse' },
  { id: 'res-003', name: 'Water Purification Tablets', category: 'WASH', current: 45000, capacity: 100000, unit: 'tablets', status: 'adequate', location: 'Dire Dawa Hub' },
  { id: 'res-004', name: 'Emergency Shelter Kits', category: 'Shelter', current: 850, capacity: 2000, unit: 'kits', status: 'adequate', location: 'Dire Dawa Hub' },
  { id: 'res-005', name: 'Hygiene Kits', category: 'WASH', current: 2100, capacity: 5000, unit: 'kits', status: 'low', location: 'Jijiga Warehouse' },
];

const mockDataSources: DataSource[] = [
  { id: 'ds-001', name: 'MOH PHEM', type: 'Health Surveillance', status: 'online', lastUpdate: '3 min ago', events: 847 },
  { id: 'ds-002', name: 'WHO EWARN Ethiopia', type: 'Health Surveillance', status: 'online', lastUpdate: '1h ago', events: 423 },
  { id: 'ds-003', name: 'Ethiopian Met Institute', type: 'Weather', status: 'online', lastUpdate: '15 min ago', events: 234 },
  { id: 'ds-004', name: 'UNOSAT Satellite', type: 'Satellite Imagery', status: 'online', lastUpdate: '20 min ago', events: 156 },
  { id: 'ds-005', name: 'FEWS NET Ethiopia', type: 'Food Security', status: 'online', lastUpdate: '45 min ago', events: 189 },
  { id: 'ds-006', name: 'NRC Field Reports', type: 'Field Intelligence', status: 'online', lastUpdate: '2h ago', events: 67 },
  { id: 'ds-007', name: 'IOM DTM Ethiopia', type: 'Displacement', status: 'delayed', lastUpdate: '4h ago', events: 234 },
  { id: 'ds-008', name: 'Social Monitoring', type: 'Social Media', status: 'online', lastUpdate: '8 min ago', events: 1247 },
];

// EWAS Sudan Data Sources (NRC EWAS Pipeline)
const ewasDataSources: DataSource[] = [
  { id: 'ewas-acled', name: 'ACLED', type: 'Conflict Monitoring', status: 'online', lastUpdate: '15 min ago', events: 22458 },
  { id: 'ewas-acled-cast', name: 'ACLED CAST', type: 'Conflict Forecast', status: 'online', lastUpdate: '2h ago', events: 228 },
  { id: 'ewas-iom-dtm', name: 'IOM DTM', type: 'Displacement Tracking', status: 'online', lastUpdate: '30 min ago', events: 31302 },
  { id: 'ewas-idmc-gidd', name: 'IDMC-GIDD', type: 'Displacement Database', status: 'online', lastUpdate: '4h ago', events: 215 },
  { id: 'ewas-idmc-idu', name: 'IDMC-IDU', type: 'Displacement Updates', status: 'online', lastUpdate: '6h ago', events: 203 },
  { id: 'ewas-fewsnet', name: 'FEWSNET', type: 'Food Security', status: 'online', lastUpdate: '12h ago', events: 208 },
  { id: 'ewas-glofas', name: 'GloFAS', type: 'Flood Awareness', status: 'online', lastUpdate: '1h ago', events: 3 },
  { id: 'ewas-dataminr', name: 'Dataminr', type: 'Social Media Alerts', status: 'online', lastUpdate: '5 min ago', events: 725 },
  { id: 'ewas-dtm-flash', name: 'DTM Flash', type: 'Flash Displacement', status: 'online', lastUpdate: '45 min ago', events: 77 },
  { id: 'ewas-reliefweb', name: 'ReliefWeb', type: 'Humanitarian Reports', status: 'online', lastUpdate: '3h ago', events: 7 },
];

const mockCashLocations: CashLocation[] = [
  {
    id: 'loc-001',
    name: 'Jijiga Town',
    region: 'Somali',
    coordinates: [42.79, 9.35],
    affectedPopulation: 45000,
    eligibleHouseholds: 8500,
    transferAmount: 3500,
    demographics: { gender: { male: 48, female: 52 }, ageGroups: { children: 42, adults: 51, elderly: 7 }, vulnerabilityScore: 78 },
    marketStatus: 'partially_functional',
    securityLevel: 'moderate',
  },
  {
    id: 'loc-002',
    name: 'Kebridehar',
    region: 'Somali',
    coordinates: [44.2, 6.73],
    affectedPopulation: 28000,
    eligibleHouseholds: 5200,
    transferAmount: 3500,
    demographics: { gender: { male: 45, female: 55 }, ageGroups: { children: 45, adults: 48, elderly: 7 }, vulnerabilityScore: 82 },
    marketStatus: 'partially_functional',
    securityLevel: 'moderate',
  },
  {
    id: 'loc-003',
    name: 'Gode',
    region: 'Somali',
    coordinates: [43.45, 5.95],
    affectedPopulation: 32000,
    eligibleHouseholds: 6100,
    transferAmount: 3200,
    demographics: { gender: { male: 47, female: 53 }, ageGroups: { children: 44, adults: 49, elderly: 7 }, vulnerabilityScore: 75 },
    marketStatus: 'functional',
    securityLevel: 'stable',
  },
  {
    id: 'loc-004',
    name: 'Dire Dawa',
    region: 'Dire Dawa',
    coordinates: [42.0, 9.6],
    affectedPopulation: 18000,
    eligibleHouseholds: 3400,
    transferAmount: 2800,
    demographics: { gender: { male: 49, female: 51 }, ageGroups: { children: 38, adults: 54, elderly: 8 }, vulnerabilityScore: 65 },
    marketStatus: 'functional',
    securityLevel: 'stable',
  },
  {
    id: 'loc-005',
    name: 'Harar',
    region: 'Harari',
    coordinates: [42.12, 9.31],
    affectedPopulation: 12000,
    eligibleHouseholds: 2200,
    transferAmount: 2500,
    demographics: { gender: { male: 46, female: 54 }, ageGroups: { children: 40, adults: 52, elderly: 8 }, vulnerabilityScore: 58 },
    marketStatus: 'functional',
    securityLevel: 'stable',
  },
];

// Filter options
export const regions = ['All Regions', 'Somali', 'Oromia', 'Afar', 'Amhara', 'Tigray', 'SNNPR', 'Dire Dawa', 'Harari'];
export const crisisTypes = ['All Types', 'Cholera', 'Flooding', 'Drought', 'Conflict', 'Displacement'];
export const severityLevels = ['All Severities', 'Critical', 'High', 'Medium', 'Low'];

// API Functions
export const wireframeMockApi = {
  // Crises
  async getCrises(filters?: { region?: string; type?: string; severity?: string }): Promise<Crisis[]> {
    await delay(300 + Math.random() * 200);
    let result = [...mockCrises];
    if (filters?.region && filters.region !== 'All Regions') {
      result = result.filter(c => c.region === filters.region);
    }
    if (filters?.type && filters.type !== 'All Types') {
      result = result.filter(c => c.type === filters.type.toLowerCase());
    }
    if (filters?.severity && filters.severity !== 'All Severities') {
      result = result.filter(c => c.severity === filters.severity.toLowerCase());
    }
    return result;
  },

  async getCrisisById(id: string): Promise<Crisis | undefined> {
    await delay(200);
    return mockCrises.find(c => c.id === id);
  },

  // Alerts
  async getAlerts(filters?: { type?: string; acknowledged?: boolean }): Promise<Alert[]> {
    await delay(300 + Math.random() * 200);
    let result = [...mockAlerts];
    if (filters?.type && filters.type !== 'All Types') {
      result = result.filter(a => a.type === filters.type.toLowerCase());
    }
    if (filters?.acknowledged !== undefined) {
      result = result.filter(a => a.acknowledged === filters.acknowledged);
    }
    return result;
  },

  async createAlert(alert: Omit<Alert, 'id' | 'timestamp'>): Promise<Alert> {
    await delay(500 + Math.random() * 300);
    const newAlert: Alert = {
      ...alert,
      id: `alert-${Date.now()}`,
      timestamp: 'Just now',
      acknowledged: false,
    };
    mockAlerts = [newAlert, ...mockAlerts];
    return newAlert;
  },

  async acknowledgeAlert(id: string): Promise<Alert | undefined> {
    await delay(300);
    const alert = mockAlerts.find(a => a.id === id);
    if (alert) {
      alert.acknowledged = true;
    }
    return alert;
  },

  // Teams
  async getTeams(filters?: { status?: string; type?: string }): Promise<Team[]> {
    await delay(300 + Math.random() * 200);
    let result = [...mockTeams];
    if (filters?.status && filters.status !== 'All') {
      result = result.filter(t => t.status === filters.status);
    }
    if (filters?.type && filters.type !== 'All') {
      result = result.filter(t => t.type === filters.type);
    }
    return result;
  },

  async deployTeam(team: Omit<Team, 'id' | 'lastCheckIn'>): Promise<Team> {
    await delay(800 + Math.random() * 400);
    const newTeam: Team = {
      ...team,
      id: `team-${Date.now()}`,
      lastCheckIn: 'Just now',
    };
    mockTeams = [...mockTeams, newTeam];
    return newTeam;
  },

  async updateTeamStatus(id: string, status: Team['status']): Promise<Team | undefined> {
    await delay(300);
    const team = mockTeams.find(t => t.id === id);
    if (team) {
      team.status = status;
      team.lastCheckIn = 'Just now';
    }
    return team;
  },

  // Resources
  async getResources(): Promise<Resource[]> {
    await delay(300 + Math.random() * 200);
    return [...mockResources];
  },

  async requestResource(resourceId: string, quantity: number): Promise<{ success: boolean; message: string }> {
    await delay(600 + Math.random() * 400);
    const resource = mockResources.find(r => r.id === resourceId);
    if (resource) {
      return { success: true, message: `Request for ${quantity} ${resource.unit} of ${resource.name} submitted.` };
    }
    return { success: false, message: 'Resource not found.' };
  },

  // Data Sources
  async getDataSources(includeEWAS: boolean = false): Promise<DataSource[]> {
    await delay(200);
    if (includeEWAS) {
      return [...mockDataSources, ...ewasDataSources];
    }
    return [...mockDataSources];
  },

  // EWAS Data Sources only
  async getEWASDataSources(): Promise<DataSource[]> {
    await delay(200);
    return [...ewasDataSources];
  },

  // Pipeline statistics
  async getPipelineStats(): Promise<{
    totalRecords: number;
    sourcesOnline: number;
    sourcesTotal: number;
    lastSync: string;
    ewasRecords: number;
    criticalAlerts: number;
  }> {
    await delay(150);
    const ewasTotal = ewasDataSources.reduce((sum, s) => sum + s.events, 0);
    const regularTotal = mockDataSources.reduce((sum, s) => sum + s.events, 0);
    return {
      totalRecords: ewasTotal + regularTotal,
      sourcesOnline: ewasDataSources.filter(s => s.status === 'online').length + mockDataSources.filter(s => s.status === 'online').length,
      sourcesTotal: ewasDataSources.length + mockDataSources.length,
      lastSync: '5 min ago',
      ewasRecords: ewasTotal,
      criticalAlerts: 12,
    };
  },

  // Cash Locations
  async getCashLocations(filters?: { region?: string }): Promise<CashLocation[]> {
    await delay(300 + Math.random() * 200);
    let result = [...mockCashLocations];
    if (filters?.region && filters.region !== 'All Regions') {
      result = result.filter(l => l.region === filters.region);
    }
    return result;
  },

  async getCashLocationById(id: string): Promise<CashLocation | undefined> {
    await delay(200);
    return mockCashLocations.find(l => l.id === id);
  },

  // Export functions (mock file generation)
  async exportToPdf(type: 'briefing' | 'analysis' | 'assessment', data: any): Promise<Blob> {
    await delay(1500 + Math.random() * 500);
    // Create a mock PDF blob (in reality this would be generated server-side)
    const content = `CLEAR Export - ${type.toUpperCase()}\n\nGenerated: ${new Date().toISOString()}\n\nThis is a mock PDF export for demonstration purposes.`;
    return new Blob([content], { type: 'application/pdf' });
  },

  async exportToCsv(type: 'alerts' | 'teams' | 'resources' | 'cash', data: any[]): Promise<Blob> {
    await delay(800 + Math.random() * 400);
    // Create a simple CSV
    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(item => Object.values(item).join(',')).join('\n');
    const content = `${headers}\n${rows}`;
    return new Blob([content], { type: 'text/csv' });
  },

  // Stats
  async getStats(): Promise<{
    activeCrises: number;
    teamsDeployed: number;
    atRiskPopulation: number;
    alertsToday: number;
    dataSourcesOnline: number;
  }> {
    await delay(200);
    return {
      activeCrises: mockCrises.filter(c => c.status === 'active').length,
      teamsDeployed: mockTeams.filter(t => t.status === 'Active' || t.status === 'En Route').length,
      atRiskPopulation: mockCrises.reduce((sum, c) => sum + c.affectedPopulation, 0),
      alertsToday: mockAlerts.length,
      dataSourcesOnline: mockDataSources.filter(d => d.status === 'online').length,
    };
  },
};

export default wireframeMockApi;
